import { type AccountSummary, Provider } from '@fruitful/types';
import { useAtomValue } from 'jotai/index';
import React, { useState } from 'react';
import {
    BodyLargeEmphasized,
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    Spinner,
    useToastController,
    XStack,
    YStack,
} from 'smoothie';

import { GroveSimpleModal } from '../../../common/components/GroveSimpleModal';
import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../../common/components/Toasts';
import { selectedMembershipAtom } from '../../entityAtoms';
import { useUpdateManualAccountAndRefetch } from '../hooks/externalAccounts/useUpdateManualAccount';
import { useUpsertOrionAccountAndRefetch } from '../hooks/externalAccounts/useUpsertOrionAccount';
import { useUpsertQuilttAccountAndRefetch } from '../hooks/externalAccounts/useUpsertQuilttAccount';
import { useUpsertSyncteraAccountAndRefetch } from '../hooks/externalAccounts/useUpsertSyncteraAccount';

export const AccountDeleteModal = ({
    account,
    isOpen,
    onClose,
}: {
    account: AccountSummary;
    isOpen: boolean;
    onClose: () => void;
}) => {
    const toast = useToastController();
    const membership = useAtomValue(selectedMembershipAtom);
    const { updateManualAccountAsync } = useUpdateManualAccountAndRefetch(
        membership?.id ?? null,
    );
    const { upsertOrionAccountAsync } = useUpsertOrionAccountAndRefetch(
        membership?.id ?? null,
    );
    const { upsertSyncteraAccountAsync } = useUpsertSyncteraAccountAndRefetch(
        membership?.id ?? null,
    );
    const { upsertQuilttAccountAsync } = useUpsertQuilttAccountAndRefetch(
        membership?.id ?? null,
    );
    const [showSpinner, setShowSpinner] = useState(false);

    const handleConfirm = async () => {
        if (account?.userId == null) {
            throw new Error('Need user ID to save account updates');
        }

        setShowSpinner(true);

        try {
            if (account.provider === Provider.MANUAL) {
                const upsertAccountBody = {
                    id: account.id,
                    userId: account.userId,
                    isHidden: true,
                };
                await updateManualAccountAsync(upsertAccountBody);
            } else if (account.provider === Provider.QUILTT) {
                const upsertAccountBody = {
                    id: account.id,
                    userId: account.userId,
                    isHidden: true,
                };

                await upsertQuilttAccountAsync(upsertAccountBody);
            } else if (account.provider === Provider.ORION) {
                const upsertAccountBody = {
                    id: account.id,
                    userId: account.userId,
                    isHidden: true,
                };

                await upsertOrionAccountAsync(upsertAccountBody);
            } else if (account.provider === Provider.SYNCTERA) {
                const upsertAccountBody = {
                    id: account.id,
                    userId: account.userId,
                    isHidden: true,
                };

                await upsertSyncteraAccountAsync(upsertAccountBody);
            }

            onClose();

            toast.show('Account deleted', MutationSuccessToastOptions);
        } catch (error) {
            toast.show('Error deleting account', MutationErrorToastOptions());
        } finally {
            setShowSpinner(false);
        }
    };

    return (
        <GroveSimpleModal isOpen={isOpen} onClose={onClose} hasCloseButton>
            <YStack gap={24} padding={24} width={500}>
                <YStack gap={8} alignItems="center">
                    <BodyLargeEmphasized color="$red10">
                        Delete Account
                    </BodyLargeEmphasized>
                    <BodyMediumRegular color="$gray10" textAlign="center">
                        Are you sure you want to delete this account? This
                        action cannot be undone.
                    </BodyMediumRegular>
                </YStack>

                <YStack
                    gap={16}
                    padding={16}
                    backgroundColor="$gray2"
                    borderRadius={12}
                >
                    <YStack gap={12}>
                        <YStack gap={4}>
                            <BodyMediumRegular fontWeight="600" color="$gray12">
                                Account Name
                            </BodyMediumRegular>
                            <BodyMediumRegular color="$gray11">
                                {account.name}
                            </BodyMediumRegular>
                        </YStack>

                        <YStack gap={4}>
                            <BodyMediumRegular fontWeight="600" color="$gray12">
                                Institution
                            </BodyMediumRegular>
                            <BodyMediumRegular color="$gray11">
                                {account.institutionName}
                            </BodyMediumRegular>
                        </YStack>

                        <YStack gap={4}>
                            <BodyMediumRegular fontWeight="600" color="$gray12">
                                Category
                            </BodyMediumRegular>
                            <BodyMediumRegular color="$gray11">
                                {account.category
                                    .replace(/_/g, ' ')
                                    .toLowerCase()
                                    .replace(/\b\w/g, (c) => c.toUpperCase())}
                            </BodyMediumRegular>
                        </YStack>

                        {account.currentBalanceCents != null && (
                            <YStack gap={4}>
                                <BodyMediumRegular
                                    fontWeight="600"
                                    color="$gray12"
                                >
                                    Current Balance
                                </BodyMediumRegular>
                                <BodyMediumRegular color="$gray11">
                                    $
                                    {(
                                        account.currentBalanceCents / 100
                                    ).toLocaleString('en-US', {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                    })}
                                </BodyMediumRegular>
                            </YStack>
                        )}

                        {account.description != null && (
                            <YStack gap={4}>
                                <BodyMediumRegular
                                    fontWeight="600"
                                    color="$gray12"
                                >
                                    Description
                                </BodyMediumRegular>
                                <BodyMediumRegular color="$gray11">
                                    {account.description}
                                </BodyMediumRegular>
                            </YStack>
                        )}
                    </YStack>
                </YStack>

                {showSpinner && (
                    <XStack gap={12}>
                        <Spinner />
                    </XStack>
                )}
                {!showSpinner && (
                    <XStack gap={12}>
                        <Button
                            flex={1}
                            variant={ButtonVariant.SECONDARY}
                            size={ButtonSize.SMALL}
                            tone="neutral"
                            text="Cancel"
                            onPress={onClose}
                        />
                        <Button
                            flex={1}
                            variant={ButtonVariant.PRIMARY}
                            size={ButtonSize.SMALL}
                            tone="danger"
                            text="Delete Account"
                            onPress={handleConfirm}
                        />
                    </XStack>
                )}
            </YStack>
        </GroveSimpleModal>
    );
};
